import React, { useContext, useEffect, useState } from 'react';

import { toJS } from 'mobx';
import { MobXProviderContext, observer } from 'mobx-react';

import { Loader } from 'semantic-ui-react';

import classNames from 'classnames';

import { kebabCase } from 'lodash';

import '../../css/GoogleClassroomConnectionModal.less';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';

import dialogManager, { DIALOG_NAMES } from '../../managers/DialogManager';

import PopupService from '../../services/PopupService';
import GoogleClassroomService from '../../services/GoogleClassroomService';
import UtilityService from '../../services/UtilityService';
import { getStyleVar } from '../../utils';

const GoogleClassroomConnectionModal = observer((props = {}) => {
  const {
    buttonKeyCancelLabel = 'cancel',
    buttonKeyConfirmLabel = 'link', // 'link' | 'unlink' | 'import'
    className = 'GoogleClassroomConnectionModal',
    closeOnDimmerClick = false,
    closeOnEscape = true,
    currentClassroom,
    disableKeystroke = false,
    disableMove = false,
    disableResize = true,
    initHeight,
    initWidth,
    minWidth = 540,
    onOpen = () => {},
    onRequestClose,
    open: isOpen,
    overlay = 'medium',
    t,
    top = window.innerHeight / 6
  } = props;

  const {
    classroomManager,
    googleClassroomManager,
  } = useContext(MobXProviderContext);

  const [isLoadingGoogleClassroomConnectionModal, setIsLoadingGoogleClassroomConnectionModal] = useState(true);

  const GenericModal = SatCoreComponent('GenericModal');
  const IndeterminateCheckbox = SatCoreComponent('IndeterminateCheckbox');

  const isLinkedGoogleClassroom = !!currentClassroom?.googleClassroomId;

  const isMultiSelectionMode = !currentClassroom;

  const classroomNameObj = currentClassroom?.id ? (
    classroomManager.getClassName(currentClassroom.id, { shouldReturnParserWrapperObj: true })
  ) : {};

  let headerMsg;
  if (isMultiSelectionMode) {
    headerMsg = t('headerMsgMultiClassrooms');
  } else if (isLinkedGoogleClassroom) {
    headerMsg = t('headerMsgLinkedClassroom');
  } else {
    headerMsg = t('headerMsgSingleClassroom');
  }

  useEffect(() => {
    (async () => {
      await GoogleClassroomService.fetchGoogleClassroomsMap();
      setIsLoadingGoogleClassroomConnectionModal(false);
    })();
  }, []);

  const handleChangeClassroomOption = (event, googleClassroomOption = {}) => {
    const classroomId = googleClassroomOption?.id;
    if (!classroomId) {
      return;
    }
    const checked = !!event?.target?.checked;

    switch (classroomId) {
      case 'selectAll':
        if (checked) {
          for (const googleClassroom of toJS(googleClassroomManager.unlinkedGoogleClassroomsArray)) {
            googleClassroomManager.addToSelectedGoogleClassroomOptionsMap(googleClassroom);
          }
        } else {
          googleClassroomManager.clearSelectedGoogleClassroomOptionsMap();
        }
        break;
      default:
        if (checked) {
          if (!isMultiSelectionMode && googleClassroomManager.selectedGoogleClassroomOptionsMap.size) {
            googleClassroomManager.clearSelectedGoogleClassroomOptionsMap();
          }
          googleClassroomManager.addToSelectedGoogleClassroomOptionsMap(googleClassroomOption);
        } else {
          googleClassroomManager.removeFromSelectedGoogleClassroomOptionsMap(googleClassroomOption?.id);
        }
        break;
    }
  };

  const handleCloseGoogleClassroomConnectionModal = async (_event, actionButtonData = {}) => {
    setIsLoadingGoogleClassroomConnectionModal(true);

    const confirmed = !!(actionButtonData?.className?.includes?.(kebabCase(buttonKeyConfirmLabel)));

    const cleanup = async ({ success } = {}) => {
      if (success) {
        googleClassroomManager.clearGoogleClassroomsMap();
        googleClassroomManager.clearSelectedGoogleClassroomOptionsMap();
        props.onClose?.();
        setIsLoadingGoogleClassroomConnectionModal(false);
        if (confirmed && typeof props.fetchClassroomData === 'function') {
          await props.fetchClassroomData();
        }
      } else {
        setIsLoadingGoogleClassroomConnectionModal(false);
      }
    };

    if (confirmed) {
      // user confirmed changes via action button
      if (isMultiSelectionMode) {
        const success = await handleImportSelectedGoogleClassrooms();
        await cleanup({ success });
      } else if (isLinkedGoogleClassroom) {
        const success = await handleDisconnectSelectedGoogleClassroom();
        await cleanup({ success });
      } else {
        const success = await handleConnectSelectedGoogleClassroom();
        await cleanup({ success });
      }
    } else {
      // user did not confirm changes
      await cleanup({ success: true });
    }
  };

  const handleImportSelectedGoogleClassrooms = async () => {
    const response = await GoogleClassroomService.importGoogleClassrooms();
    if (!response?.success) {
      renderErrorModal({ success: response?.success });
    }
    return !!(response?.success);
  };

  const handleConnectSelectedGoogleClassroom = async () => {
    const currentGoogleClassroom = googleClassroomManager.selectedGoogleClassroomsArray?.[0];

    const response = await GoogleClassroomService.connectGoogleToClassroom({
      classroom: currentClassroom,
      googleClassroom: currentGoogleClassroom
    });
    if (!response?.success) {
      renderErrorModal({ success: response?.success });
    }
    return !!(response?.success);
  };

  const handleDisconnectSelectedGoogleClassroom = async () => {
    const response = await GoogleClassroomService.disconnectGoogleFromClassroom({
      classroom: currentClassroom
    });
    if (!response?.success) {
      renderErrorModal({ success: response?.success });
    }
    return !!(response?.success);
  };

  const renderGoogleClassroomConnectionModalBody = () => {
    if (isLoadingGoogleClassroomConnectionModal) {
      return <Loader active />;
    }

    const hasUnlinkedGoogleClassrooms = !!googleClassroomManager.unlinkedGoogleClassroomsArray?.length;

    let googleClassroomConnectionModalBodyHeaderLabel;
    if (!hasUnlinkedGoogleClassrooms && !isLinkedGoogleClassroom) {
      googleClassroomConnectionModalBodyHeaderLabel = isMultiSelectionMode ? (
        t('zeroStateMultiGoogleClassroomConnectionModalBodyHeaderLabel')
      ) : (
        t('zeroStateSingleGoogleClassroomConnectionModalBodyHeaderLabel')
      );
    } else if (isMultiSelectionMode) {
      googleClassroomConnectionModalBodyHeaderLabel = t('multiGoogleClassroomConnectionModalBodyHeaderLabel');
    } else if (isLinkedGoogleClassroom) {
      // const classroomNameObj = classroomManager.getClassName(currentClassroom.id, { shouldReturnParserWrapperObj: true });
      googleClassroomConnectionModalBodyHeaderLabel = t('linkedGoogleClassroomConnectionModalBodyHeaderLabel', {
        currentClassroomName: classroomNameObj.stripped,
      });
    } else {
      // const classroomNameObj = classroomManager.getClassName(currentClassroom.id, { shouldReturnParserWrapperObj: true });
      googleClassroomConnectionModalBodyHeaderLabel = t('singleGoogleClassroomConnectionModalBodyHeaderLabel', {
        currentClassroomName: classroomNameObj.stripped,
      });
    }

    return (
      <>
        <div className='google-classroom-connection-modal-body-top'>
          <div className='google-classroom-connection-modal-body-top-left'>
            <div className='google-classroom-connection-modal-body-header-label'>
              {t(googleClassroomConnectionModalBodyHeaderLabel)}
            </div>
          </div>
          <div className='google-classroom-connection-modal-body-top-mid'>
            {/* placeholder */}
          </div>
          <div className='google-classroom-connection-modal-body-top-right'>
            {/* TODO remove */}
            {/* <div className='google-classroom-connection-reset-all'
              onClick={() => {
                googleClassroomManager.clearSelectedGoogleClassroomOptionsMap();
              }}>
              {t('resetAll')}
            </div> */}
          </div>
        </div>
        <div className='google-classroom-connection-modal-options'>
          {isMultiSelectionMode && hasUnlinkedGoogleClassrooms && (
            renderSelectAllGoogleClassroomsOption()
          )}
          {renderGoogleClassroomOptions()}
        </div>
      </>
    );
  };

  const renderSelectAllGoogleClassroomsOption = () => {
    const {
      // googleClassroomsMap,
      selectedGoogleClassroomOptionsMap,
      unlinkedGoogleClassroomsArray
    } = googleClassroomManager;
    const option = {
      id: 'selectAll'
    };
    const isAllSelectedClassrooms = unlinkedGoogleClassroomsArray.length && (
      unlinkedGoogleClassroomsArray.length === selectedGoogleClassroomOptionsMap.size
    );

    const checked = isAllSelectedClassrooms;
    const disabled = false;

    return (
      <div className={classNames('google-classroom-connection-modal-option-row', {
        'google-classroom-connection-modal-option-row-selectAll': true
      })}>
        <div className={classNames('google-classroom-option-checkbox-wrapper')}>
          <IndeterminateCheckbox
            aria-label={t('selectAll')}
            checked={checked}
            disabled={disabled}
            onChange={(event) => handleChangeClassroomOption(event, option)} />
          <div className={classNames('checkbox-label google-classroom-option-checkbox-label', {
            checked: isAllSelectedClassrooms,
            disabled
          })} onClick={() => {
            if (!disabled) {
              const pseudoEvent = {
                target: { checked: !checked }
              };
              handleChangeClassroomOption(pseudoEvent, option);
            }
          }}>
            {t('selectAll')}
          </div>
        </div>
      </div>
    );
  };

  const renderGoogleClassroomOptions = () => {
    if (isLinkedGoogleClassroom) {
      const currentGoogleClassroom = googleClassroomManager.googleClassroomsMap.get(currentClassroom.googleClassroomId);
      // render the linked current classroom name as a readonly (disabled) option
      // (to show the user it is the current 'linked' google classroom)
      return [currentGoogleClassroom].map((googleClassroom) => {
        return renderGoogleClassroomOption(googleClassroom, { disabled: true, forceSelected: true });
      });
    } else {
      return googleClassroomManager.unlinkedGoogleClassroomsArray?.map((googleClassroom) => {
        return renderGoogleClassroomOption(googleClassroom);
      });
    }
  };

  const renderGoogleClassroomOption = (googleClassroom, {
    forceSelected,
    disabled
  } = {}) => {
    const {
      selectedGoogleClassroomOptionsMap
    } = googleClassroomManager;
    if (!googleClassroom?.id) {
      return null;
    }
    const googleClassroomNameObj = UtilityService.reactHtmlParserWrapper(googleClassroom.name);

    const checked = forceSelected || selectedGoogleClassroomOptionsMap.has(googleClassroom.id);

    return googleClassroomNameObj ? (
      <div key={googleClassroom.id}
        className={classNames('google-classroom-connection-modal-option-row')}>
        <div className={classNames('google-classroom-option-checkbox-wrapper')}>
          <IndeterminateCheckbox
            aria-label={googleClassroomNameObj.stripped}
            checked={checked}
            disabled={disabled}
            onChange={(event) => handleChangeClassroomOption(event, googleClassroom)}
            radio={!isMultiSelectionMode} />
          {PopupService.renderPopup({
            className: 'google-classroom-option-checkbox-label-popup',
            content: googleClassroomNameObj.parsed,
            mouseEnterDelay: 1000,
            position: 'top left',
            trigger: (
              <div className={classNames('checkbox-label google-classroom-option-checkbox-label', {
                checked,
                disabled
              })} onClick={() => {
                if (!disabled) {
                  const pseudoEvent = {
                    target: { checked: !checked }
                  };
                  handleChangeClassroomOption(pseudoEvent, googleClassroom);
                }
              }}>
                {googleClassroomNameObj.stripped}
              </div>
            ),
            wide: false
          })}
        </div>
      </div>
    ) : null;
  };

  const renderErrorModal = ({
    success,
    ...props
  } = {}) => {
    renderSuccessOrFailureModal({ ...props, success });
  };

  const renderSuccessOrFailureModal = ({
    cancelButtonClassName = '',
    cancelButtonName = '',
    className = '',
    confirmButtonClassName = '',
    confirmButtonName = '',
    confirmHandler = () => {},
    message = '',
    success = false,
    title = '',
  } = {}) => {
    dialogManager.setOpenDialog(DIALOG_NAMES.CONFIRM, {
      cancelButtonClass: cancelButtonClassName,
      cancelButtonName: cancelButtonName || t('close'),
      className: className || classNames('google-classroom-connection-confirm-modal', {
        error: !success,
        success
      }),
      confirmButtonClass: confirmButtonClassName,
      confirmButtonName,
      confirmHandler,
      message: message || t('defaultErrorMessage'),
      title: title || t('defaultErrorHeaderLabel')
    },
    () => dialogManager.closeDialog(DIALOG_NAMES.CONFIRM));
  };

  const disableConfirmButton = !googleClassroomManager.selectedGoogleClassroomOptionsMap.size && !isLinkedGoogleClassroom;

  let minHeight, isZeroState;
  if (props.minHeight) {
    minHeight = props.minHeight;
  } else if (isLinkedGoogleClassroom) {
    minHeight = getStyleVar('--google-classroom-connection-modal-linked-min-height');
  } else if (!isLoadingGoogleClassroomConnectionModal && !googleClassroomManager.unlinkedGoogleClassroomsArray?.length) {
    isZeroState = true;
    minHeight = getStyleVar('--google-classroom-connection-modal-zerostate-min-height');
  } else {
    minHeight = getStyleVar('--google-classroom-connection-modal-min-height');
  }

  return (
    <GenericModal
      bodyMsg={renderGoogleClassroomConnectionModalBody}
      buttonKeyCancelLabel={buttonKeyCancelLabel}
      buttonKeyConfirmLabel={!isZeroState ? buttonKeyConfirmLabel : ''}
      className={classNames(className, {
        'zero-state': isZeroState
      })}
      closeOnDimmerClick={closeOnDimmerClick}
      closeOnEscape={closeOnEscape}
      disableConfirmButton={disableConfirmButton}
      disableKeystroke={disableKeystroke}
      disableMove={disableMove}
      disableResize={disableResize}
      headerMsg={headerMsg}
      initHeight={initHeight || minHeight}
      initWidth={initWidth || minWidth}
      isConfirmButtonLoading={false}
      isLoading={isLoadingGoogleClassroomConnectionModal}
      isOpen={isOpen}
      onClose={handleCloseGoogleClassroomConnectionModal}
      onOpen={onOpen}
      onRequestClose={onRequestClose}
      overlay={overlay}
      top={top} />
  );
});

export default GoogleClassroomConnectionModal;

SatCoreRegister('GoogleClassroomConnectionModal', GoogleClassroomConnectionModal);
